<template>
    <v-form
        ref="form"
        @submit.prevent="onSubmit"
    >
        <v-row>
            <v-col
                cols="12"
                class="text-center"
            >
                <v-icon
                    color="primary"
                    size="120"
                >
                    emoji_people
                </v-icon>
                <h2 class="display-1 mb-2">
                    You're Verified!
                </h2>
                <p class="body-1 font-weight-light">
                    Now let's pick your handle so others can find you.
                </p>
                <p class="body-1 font-weight-light">
                    Please choose a handle between 3 and 50 characters. Your handle may contain letters, numbers, dashes "-" and underscores "_"
                </p>
            </v-col>

            <v-col cols="12">
                <v-text-field
                    v-model="handle"
                    :label="$vuetify.breakpoint.smAndDown ? 'comeconnect.me/' : ''"
                    :rules="[
                        rules.min,
                        rules.max,
                        rules.allowedCharacters
                    ]"
                    color="secondary"
                    :loading="isLoadingHandle"
                    @input="onInput"
                >
                    <template v-slot:prepend v-if="$vuetify.breakpoint.mdAndUp">
                        comeconnect.me/
                    </template>
                    <template v-slot:append>
                        <v-tooltip
                            top
                            :value="handleInput.tooltipVisible"
                        >
                            <template v-slot:activator="{ on }">
                                <v-icon
                                    v-on="on"
                                    :color="handleInput.iconColor"
                                >
                                    {{ handleInput.icon }}
                                </v-icon>
                            </template>
                            {{ handleInput.text }}
                        </v-tooltip>
                    </template>
                </v-text-field>
            </v-col>

            <v-col cols="12">
                <div class="d-flex">
                    <v-spacer />
                    <v-btn
                        type="submit"
                        :loading="isSubmitting"
                        :disabled="submitIsDisabled"
                        block
                        large
                        color="primary"
                    >
                        Submit
                    </v-btn>
                </div>
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import axios from 'axios'
import UserBus from '../../utils/buses/UserBus'
import MessageBus from '../../utils/buses/MessageBus'
import { default422Error } from '../../utils/constants/errors'
import {
    minHandleValidation,
    maxHandleValidation,
    allowedCharactersHandleValidation
} from '../../utils/validations'
import debounce from 'lodash/debounce'

export default {
    data () {
        return {
            handle: null,
            isSubmitting: false,
            isLoadingHandle: false,
            handleAvailableFlag: false,
            rules: {
                min: minHandleValidation,
                max: maxHandleValidation,
                allowedCharacters: allowedCharactersHandleValidation
            },
            handleMap: {},
            cancelToken: null,
            i: 0
        }
    },

    computed: {
        submitIsDisabled () {
            const handleKey = this.handle ? this.handle.toLowerCase() : null
            // disable submit if handle has not been checked if available
            // or if handle has been checked but is not avaiable
            return !this.handleMap.hasOwnProperty(handleKey) || this.handleMap[handleKey] !== true
        },
        handleInput () {
            const handleKey = this.handle ? this.handle.toLowerCase() : null
            // if check handle api is loading or handle is invalid, return default values
            if (
                this.isLoadingHandle
                // TODO - replace with single input validation
                // https://github.com/vuetifyjs/vuetify/issues/8698 when available
                || minHandleValidation(handleKey) !== true
                || maxHandleValidation(handleKey) !== true
                || allowedCharactersHandleValidation(handleKey) !== true
            ) {
                return {
                    icon: '',
                    iconColor: '',
                    text: '',
                    tooltipVisible: false
                }
            } else if (
                handleKey
                && this.handleMap.hasOwnProperty(handleKey)
                && this.handleMap[handleKey] === true
            ) {
                return {
                    icon: 'check',
                    iconColor: 'green',
                    text: 'Handle is available',
                    tooltipVisible: false
                }
            } else if (
                handleKey
                && this.handleMap.hasOwnProperty(handleKey)
                && this.handleMap[handleKey] === false
            ) {
                return {
                    icon: 'not_interested',
                    iconColor: 'red',
                    text: 'Handle is not available',
                    tooltipVisible: true
                }
            } else {
                return {
                    icon: '',
                    iconColor: '',
                    text: '',
                    tooltipVisible: false
                }
            }
        }
    },

    methods: {
        onInput: debounce(async function (val) {
            const handleKey = val ? val.toLowerCase() : null
            // exit method when handle is invalid
            if (
                // TODO - replace with single input validation
                // https://github.com/vuetifyjs/vuetify/issues/8698 when available
                minHandleValidation(handleKey) !== true
                || maxHandleValidation(handleKey) !== true
                || allowedCharactersHandleValidation(handleKey) !== true
            ) {
                return
            }

            // exit method, if handle has already checked
            if (
                this.handleMap.hasOwnProperty(handleKey)
            ) {
                return
            }

            if (this.cancelToken) {
                this.cancelToken.cancel('Request cancelled' + ' ' + this.i)
            }
            this.i++

            this.isLoadingHandle = true

            try {
                const source = axios.CancelToken.source()
                // store for later use
                this.cancelToken = source

                const res = await axios.get('/auth/check-handle', {
                    params: {
                        handle: handleKey
                    },
                    cancelToken: source.token
                })

                const { available } = res.data
                this.handleAvailableFlag = !available
                // cache results
                this.handleMap = {
                    ...this.handleMap,
                    [handleKey]: available
                }
                // set loading to false here instead of finally to avoid situation where cancelled api disable loading
                this.isLoadingHandle = false
            } catch (err) {
                // if error is caused by cancel, ignore rest of handler
                if (axios.isCancel(err)) {
                    return
                }

                MessageBus.sendMessage('Error determining if handle is unique. Please try again.', 'error')

                this.isLoadingHandle = false
            }
        }, 400),

        onSubmit () {
            if (!this.$refs.form.validate()) {
                MessageBus.sendMessage(default422Error, 'error')
                return
            }

            this.isSubmitting = true

            axios.put('/user/handle', {
                handle: this.handle
            })
                .then(res => {
                    const { token } = res.data
                    UserBus.setToken(token)

                    MessageBus.sendMessage(`Welcome ${this.handle}! Start creating your links.`, 'success')
                    this.$router.push({
                        name: 'profile',
                        params: {
                            handle: this.handle
                        }
                    })
                })
                .catch(err => {
                    console.error('Set Handle Error', err)
                    if (
                        err.status === 409
                        && err.data
                        && err.data.field
                        && err.data.field === 'handle'
                    ) {
                        MessageBus.sendMessage('Handle is already in use.', 'error')
                        return
                    }
                    MessageBus.sendMessage('Error assigning handle. Please try again.', 'error')
                })
                .finally(() => {
                    this.isSubmitting = false
                })
        }
    }
}
</script>
